import React, { useEffect, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Button from 'components/Button';
import TextArea from 'components/TextArea';
import FormContainer from 'components/LoanForm/FormContainer';
import FormNavigation from 'components/FormNavigation';

import { FeedbackResult } from 'enums/FlowNextResults';
import { FlowComponentType } from 'routes/types';
import { getApplicationData } from 'selectors/getApplicationData';

import styles from './Feedback.module.scss';

enum Fields {
  Experience = 'experience',
  HowDidYouHear = 'how_did_you_hear',
}

export interface FeedbackState {
  fromCardFlow?: boolean;
}

const textareaFields = [
  {
    label: 'Why don’t you want to sign up for the waitlist?',
    name: Fields.Experience,
    placeholder: 'Tell us anything on your mind.',
  },
  {
    label: 'What could we change that would convince you to join?',
    name: Fields.HowDidYouHear,
    placeholder: 'Any details are welcome.',
  },
];

const Feedback = ({ handleNext, navigationInfo }: FlowComponentType) => {
  const { application } = useSelector(getApplicationData);
  const { status: applicationStatus } = application ?? {};
  const [isLoading, setIsLoading] = useState(false);

  const { register, setValue, watch } = useForm({
    defaultValues: {
      [Fields.Experience]: '',
      [Fields.HowDidYouHear]: '',
    },
  });

  const watcher = watch();

  useEffect(() => {
    register(Fields.Experience);
    register(Fields.HowDidYouHear);
  }, [register, watcher]);

  const handleFieldOnChange = useCallback((event, fieldName: Fields) => {
    setValue(fieldName, event.target.value);
  }, []);

  const handleContinue = useCallback(async () => {
    setIsLoading(true);
    if (watcher[Fields.Experience] || watcher[Fields.HowDidYouHear]) {
      analytics.track('Card Feedback Submitted', {
        waitListOptOutReason: watcher[Fields.Experience],
        waitListImprovementSuggestion: watcher[Fields.HowDidYouHear],
      });
    }
    setIsLoading(false);
    if (!applicationStatus) {
      handleNext(FeedbackResult.CrossSell);
    } else {
      handleNext();
    }
  }, [watcher, applicationStatus, handleNext]);

  return (
    <>
      <FormNavigation {...navigationInfo} />
      <FormContainer title="We’d love your feedback.">
        {textareaFields.map((field) => (
          <TextArea
            key={field.name}
            label={field.label}
            placeholder={field.placeholder}
            className={styles.formInput}
            name={field.name}
            onChange={(event) => handleFieldOnChange(event, field.name)}
            value={watcher[field.name]}
          />
        ))}

        <Button onClick={handleContinue} isLoading={isLoading}>
          Next
        </Button>
      </FormContainer>
    </>
  );
};

export default Feedback;
